import { Action } from "redux";

import { BlogPostFormData } from "@/mk2/apps/blogs/components/BlogPostCreateForm";
import { BoxedHashtags } from "@/mk2/apps/blogs/components/HashtagsBox";
import { PageRef } from "@/mk2/apps/forum/schemas";
import { APIFailureAction, APIRequestAction, APISuccessAction } from "@/mk2/helpers/action";
import { NormalizedResponse } from "@/mk2/helpers/api";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";

import { OrderBy, ScrollToPosition } from "./GroupFeedPage";

///////////////////////////////////////////////////////////////////
// Progress broadcast

export interface GroupFeedFetchAction extends Action {
    readonly groupId: number;
    readonly pageRef: PageRef;
    readonly orderBy: OrderBy;
    readonly query: string;
}

/////////////////////////////////////////////////////////////////////
// Group feed load
export const GROUP_FEED_FETCH_TRIGGER = "GROUP_FEED_FETCH_TRIGGER";
export interface GroupFeedFetchTriggerAction extends GroupFeedFetchAction {
    readonly type: typeof GROUP_FEED_FETCH_TRIGGER;
    readonly location: HistoryLocationWithState;
    readonly numPostsBeforeLoad: number;
}
export const GROUP_FEED_FETCH_REQUEST = "GROUP_FEED_FETCH_REQUEST";
export interface GroupFeedFetchRequestAction extends GroupFeedFetchAction, APIRequestAction {
    readonly type: typeof GROUP_FEED_FETCH_REQUEST;
}
export interface GroupFeedFetchNormalizedResponse extends NormalizedResponse {
    readonly result: {
        complaints: number[];
        group: number;
        neighbourGroups: number[];
        posts: number[];
        allPosts: number[];
        moderators: number[];
        activeBrandDays: { posts: number[]; campaign: number };
        userComment: boolean;
        userPosts: boolean;
        isClosed: boolean;
        commentCount: number;

        eshopProductsHeader: number[];
        eshopProducts: number[];
        eshopProductsFooter: number[];

        promoReviewArticlesHeader: number[];
        promoReviewArticlesMiddle: number[];
        promoReviewArticlesFooter: number[];
    };
}
export const GROUP_FEED_FETCH_SUCCESS = "GROUP_FEED_FETCH_SUCCESS";
export interface GroupFeedFetchSuccessAction
    extends GroupFeedFetchAction, APISuccessAction<GroupFeedFetchNormalizedResponse> {
    readonly type: typeof GROUP_FEED_FETCH_SUCCESS;
    readonly page: number;
    readonly numberOfPages: number;
    readonly count: number;
    readonly memberships: number[];
    readonly askedToJoin: number[];
    readonly favorites: number[];
    readonly isBlocked: boolean;
    readonly isSubscribed: boolean;
    readonly hasComplaints: boolean;
    readonly hashtags: BoxedHashtags;
    readonly countyId: number;
    readonly productsCount: number;
    readonly userComment: boolean;
    readonly userPosts: boolean;
    readonly isClosed: boolean;
    readonly commentCount: number;
}
export const GROUP_FEED_FETCH_FAILURE = "GROUP_FEED_FETCH_FAILURE";
export interface GroupFeedFetchFailureAction extends GroupFeedFetchAction, APIFailureAction {
    readonly type: typeof GROUP_FEED_FETCH_FAILURE;
}

export const groupFeedFetchTrigger = (
    groupId: number,
    pageRef: PageRef,
    orderBy: OrderBy,
    query: string,
    location: HistoryLocationWithState,
    numCommentsBeforeLoad: number = 0,
): GroupFeedFetchTriggerAction => ({
    type: GROUP_FEED_FETCH_TRIGGER,
    groupId,
    pageRef,
    orderBy,
    query,
    location,
    numPostsBeforeLoad: numCommentsBeforeLoad,
});
export const groupFeedFetchApi = {
    request: (
        groupId: number,
        pageRef: PageRef,
        orderBy: OrderBy,
        query: string,
    ): GroupFeedFetchRequestAction => ({
        type: GROUP_FEED_FETCH_REQUEST,
        groupId,
        pageRef,
        orderBy,
        query,
    }),
    success: (
        groupId: number,
        pageRef: PageRef,
        orderBy: OrderBy,
        query: string,
        page: number,
        numberOfPages: number,
        count: number,
        response: GroupFeedFetchNormalizedResponse,
        memberships: number[],
        askedToJoin: number[],
        favorites: number[],
        isBlocked: boolean,
        isSubscribed: boolean,
        hasComplaints: boolean,
        hashtags: BoxedHashtags,
        countyId: number,
        productsCount: number,
        userComment: boolean,
        userPosts: boolean,
        isClosed: boolean,
        commentCount: number,
    ): GroupFeedFetchSuccessAction => ({
        type: GROUP_FEED_FETCH_SUCCESS,
        groupId,
        pageRef,
        orderBy,
        query,
        page,
        numberOfPages,
        count,
        response,
        memberships,
        askedToJoin,
        favorites,
        isBlocked,
        isSubscribed,
        hasComplaints,
        hashtags,
        countyId,
        productsCount,
        userComment,
        userPosts,
        isClosed,
        commentCount,
    }),
    failure: (
        groupId: number,
        pageRef: PageRef,
        orderBy: OrderBy,
        query: string,
        error: string,
    ): GroupFeedFetchFailureAction => ({
        type: GROUP_FEED_FETCH_FAILURE,
        groupId,
        pageRef,
        orderBy,
        query,
        error,
    }),
};

/////////////////////////////////////////////////////////////////////
// Group feed - scrollto

export const GROUP_FEED_SCROLLTO_TRIGGER = "GROUP_FEED_SCROLLTO_TRIGGER";
export interface GroupFeedScrollToTriggerAction {
    readonly type: typeof GROUP_FEED_SCROLLTO_TRIGGER;
    readonly scrollto: ScrollToPosition;
}

export const groupFeedScrollToTrigger = (
    scrollto: ScrollToPosition,
): GroupFeedScrollToTriggerAction => ({
    type: GROUP_FEED_SCROLLTO_TRIGGER,
    scrollto,
});

///////////////////////////////////////////////////////////////////
// Group Post Create

export const GROUP_POST_CREATE_TRIGGER = "GROUP_POST_CREATE_TRIGGER";
export interface GroupPostCreateTriggerAction {
    readonly type: typeof GROUP_POST_CREATE_TRIGGER;
    readonly groupId: number;
    readonly pageRef: PageRef;
    readonly postFd: BlogPostFormData;
    readonly location: HistoryLocationWithState;
}
export const GROUP_POST_CREATE_REQUEST = "GROUP_POST_CREATE_REQUEST";
export interface GroupPostCreateRequestAction extends APIRequestAction {
    readonly type: typeof GROUP_POST_CREATE_REQUEST;
    readonly groupId: number;
    readonly postFd: BlogPostFormData;
}
export interface GroupPostCreateNormalizedResponse extends NormalizedResponse {
    readonly result: {
        post: number;
    };
}
export const GROUP_POST_CREATE_SUCCESS = "GROUP_POST_CREATE_SUCCESS";
export interface GroupPostCreateSuccessAction extends APISuccessAction<GroupPostCreateNormalizedResponse> {
    readonly type: typeof GROUP_POST_CREATE_SUCCESS;
    readonly groupId: number;
    readonly postFd: BlogPostFormData;
}
export const GROUP_POST_CREATE_FAILURE = "GROUP_POST_CREATE_FAILURE";
export interface GroupPostCreateFailureAction extends APIFailureAction {
    readonly type: typeof GROUP_POST_CREATE_FAILURE;
    readonly groupId: number;
    readonly postFd: BlogPostFormData;
}

export const groupPostCreateTrigger = (
    groupId: number,
    pageRef: PageRef,
    postFd: BlogPostFormData,
    location: HistoryLocationWithState,
): GroupPostCreateTriggerAction => ({
    type: GROUP_POST_CREATE_TRIGGER,
    groupId,
    pageRef,
    postFd,
    location,
});
export const groupPostCreateApi = {
    request: (groupId: number, postFd: BlogPostFormData): GroupPostCreateRequestAction => ({
        type: GROUP_POST_CREATE_REQUEST,
        groupId,
        postFd,
    }),
    success: (
        groupId: number,
        postFd: BlogPostFormData,
        response: GroupPostCreateNormalizedResponse,
    ): GroupPostCreateSuccessAction => ({
        type: GROUP_POST_CREATE_SUCCESS,
        groupId,
        postFd,
        response,
    }),
    failure: (
        groupId: number,
        postFd: BlogPostFormData,
        error: string,
    ): GroupPostCreateFailureAction => ({
        type: GROUP_POST_CREATE_FAILURE,
        groupId,
        postFd,
        error,
    }),
};

////////////////////////////////////////////////////////////////////
// Send hearth
export interface GroupGuideCompleteTriggerAction extends Action {
    readonly groupId: number;
    readonly isClosedByUser: boolean;
}
export const GROUP_GUIDE_COMPLETE_TRIGGER = "GROUP_GUIDE_COMPLETE_TRIGGER";
export const GROUP_GUIDE_COMPLETE_REQUEST = "GROUP_GUIDE_COMPLETE_REQUEST";

export interface GroupGuideCompleteRequestAction
    extends GroupGuideCompleteTriggerAction, APIRequestAction {
    readonly type: typeof GROUP_GUIDE_COMPLETE_REQUEST;
}

export interface GroupGuideCompleteNormalizedResponse extends NormalizedResponse {
    readonly result: { isClosed: boolean };
}

export const GROUP_GUIDE_COMPLETE_SUCCESS = "GROUP_GUIDE_COMPLETE_SUCCESS";
export interface GroupGuideCompleteSuccessAction
    extends
        GroupGuideCompleteTriggerAction,
        APISuccessAction<GroupGuideCompleteNormalizedResponse> {
    readonly type: typeof GROUP_GUIDE_COMPLETE_SUCCESS;
    readonly isClosed: boolean;
}

export const GROUP_GUIDE_COMPLETE_FAILURE = "GROUP_GUIDE_COMPLETE_FAILURE";
export interface GroupGuideCompleteFailureAction
    extends GroupGuideCompleteTriggerAction, APIFailureAction {
    readonly type: typeof GROUP_GUIDE_COMPLETE_FAILURE;
}

export const groupGuideCompleteTrigger = (
    groupId: number,
    isClosedByUser: boolean,
): GroupGuideCompleteTriggerAction => ({
    type: GROUP_GUIDE_COMPLETE_TRIGGER,
    groupId,
    isClosedByUser,
});
export const groupGuideCompleteApi = {
    request: (groupId: number, isClosedByUser: boolean): GroupGuideCompleteRequestAction => ({
        type: GROUP_GUIDE_COMPLETE_REQUEST,
        groupId,
        isClosedByUser,
    }),
    success: (
        groupId: number,
        isClosedByUser: boolean,
        response: GroupGuideCompleteNormalizedResponse,
        isClosed: boolean,
    ): GroupGuideCompleteSuccessAction => ({
        type: GROUP_GUIDE_COMPLETE_SUCCESS,
        groupId,
        isClosedByUser,
        response,
        isClosed,
    }),
    failure: (
        groupId: number,
        isClosedByUser: boolean,
        error: string,
    ): GroupGuideCompleteFailureAction => ({
        type: GROUP_GUIDE_COMPLETE_FAILURE,
        groupId,
        isClosedByUser,
        error,
    }),
};

/////////////////////////////////////////////////////////////////////
// Group Delete
export interface GroupDeleteGroupApiAction extends Action {
    readonly groupId: number;
}

export const GROUP_DELETE_GROUP_API_REQUEST = "GROUP_DELETE_GROUP_API_REQUEST";

export interface GroupDeleteGroupApiRequestAction
    extends GroupDeleteGroupApiAction, APIRequestAction {
    readonly type: typeof GROUP_DELETE_GROUP_API_REQUEST;
}

export const GROUP_DELETE_GROUP_API_SUCCESS = "GROUP_DELETE_GROUP_API_SUCCESS";

export interface GroupDeleteNormalizedResponse extends NormalizedResponse {
    readonly result: { group: number; isMember: boolean };
}

export interface GroupDeleteGroupApiSuccessAction
    extends GroupDeleteGroupApiAction, APISuccessAction<GroupDeleteNormalizedResponse> {
    readonly type: typeof GROUP_DELETE_GROUP_API_SUCCESS;
}

export const GROUP_DELETE_GROUP_API_FAILURE = "GROUP_DELETE_GROUP_API_FAILURE";

export interface GroupDeleteGroupApiFailureAction
    extends GroupDeleteGroupApiAction, APIFailureAction {
    readonly type: typeof GROUP_DELETE_GROUP_API_FAILURE;
}

export const groupDeleteGroupApi = {
    request: (groupId: number): GroupDeleteGroupApiRequestAction => ({
        type: GROUP_DELETE_GROUP_API_REQUEST,
        groupId,
    }),
    success: (
        groupId: number,
        response: GroupDeleteNormalizedResponse,
    ): GroupDeleteGroupApiSuccessAction => ({
        type: GROUP_DELETE_GROUP_API_SUCCESS,
        groupId,
        response,
    }),
    failure: (groupId: number, error: string): GroupDeleteGroupApiFailureAction => ({
        type: GROUP_DELETE_GROUP_API_FAILURE,
        groupId,
        error,
    }),
};

export const GROUP_DELETE_GROUP = "GROUP_DELETE_GROUP";

export interface GroupDeleteGroupAction extends GroupDeleteGroupApiAction {
    readonly type: typeof GROUP_DELETE_GROUP;
}

export const groupDeleteGroup = (groupId: number): GroupDeleteGroupAction => ({
    type: GROUP_DELETE_GROUP,
    groupId,
});
